import { ProviderGamesModel } from './ProviderGames.model';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { assertNever } from 'src_common/common/assertNever';
import { CollectionType, ProviderGameType, ProviderType } from 'src/domains/casino/models/models';
import { TrpcClient } from 'src/AppState/TrpcClient';
import {
    blueprintToProviderGamesModel,
    convertNewGameModelToLegacy,
    evolutionToProviderGamesModel,
    ezugiToProviderGamesModel,
    leapToProviderGamesModel,
    oldPragmaticToProviderGamesModel,
    pragmaticToProviderGamesModel,
    relaxToProviderGamesModel,
    sgDigitalToProviderGamesModel,
} from './convertModels';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { Common } from 'src/AppState/Common';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { apiGetGamesRequest } from 'src/api_openapi/v2/casino-store/apiGetGames';

const PROVIDER_GAMES_RESOURCE_TIMEOUT_MS = 60_000;

const createProviderGamesResource = (
    loadValue: () => Promise<Array<ProviderGameType> | null>
): Resource<Array<ProviderGameType> | null> => {
    return new Resource(loadValue, PROVIDER_GAMES_RESOURCE_TIMEOUT_MS);
};

const getProviderDataLegacy = (
    trpc: TrpcClient,
    provider: ProviderType,
    configState: ConfigState
): ProviderGamesModel => {
    if (provider === 'blueprint') {
        const resource = createProviderGamesResource(async () => {
            const response = await trpc.client.providerGamesRouter.getBlueprintGames.mutate();
            if (response.status === 200) {
                return blueprintToProviderGamesModel(response.body);
            }
            return [];
        });
        return new ProviderGamesModel(resource, provider);
    }

    if (provider === 'evolution') {
        const resource = createProviderGamesResource(async () => {
            const response = await trpc.client.providerGamesRouter.getEvolutionGames.mutate();
            if (response.status === 200) {
                return evolutionToProviderGamesModel(response.body);
            }
            return [];
        });
        return new ProviderGamesModel(resource, provider);
    }

    if (provider === 'ezugi') {
        const resource = createProviderGamesResource(async () => {
            const response = await trpc.client.providerGamesRouter.getEzugiGames.mutate();
            if (response.status === 200) {
                return ezugiToProviderGamesModel(response.body);
            }
            return [];
        });
        return new ProviderGamesModel(resource, provider);
    }

    if (provider === 'leap') {
        const resource = createProviderGamesResource(async () => {
            const response = await trpc.client.providerGamesRouter.getLeapGames.mutate();
            if (response.status === 200) {
                return leapToProviderGamesModel(response.body);
            }
            return [];
        });
        return new ProviderGamesModel(resource, provider);
    }

    if (provider === 'pragmatic') {
        if (configState.config.newPragmatic === false) {
            const resource = createProviderGamesResource(async () => {
                const response = await trpc.client.providerGamesRouter.getOldPragmaticGames.mutate();
                if (response.status === 200) {
                    return oldPragmaticToProviderGamesModel(response.body);
                }
                return [];
            });
            return new ProviderGamesModel(resource, provider);
        }
        const resource = createProviderGamesResource(async () => {
            const response = await trpc.client.providerGamesRouter.getPragmaticGames.mutate();
            if (response.status === 200) {
                return pragmaticToProviderGamesModel(response.body);
            }
            return [];
        });
        return new ProviderGamesModel(resource, provider);
    }

    if (provider === 'relax') {
        const resource = createProviderGamesResource(async () => {
            const response = await trpc.client.providerGamesRouter.getRelaxGames.mutate();
            if (response.status === 200) {
                return relaxToProviderGamesModel(response.body);
            }
            return [];
        });
        return new ProviderGamesModel(resource, provider);
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (provider === 'sg-digital') {
        const resource = createProviderGamesResource(async () => {
            const response = await trpc.client.providerGamesRouter.getSGDigitalGames.mutate();
            if (response.status === 200) {
                return sgDigitalToProviderGamesModel(response.body);
            }
            return [];
        });
        return new ProviderGamesModel(resource, provider);
    }

    return assertNever('getProviderData - provider', provider);
};

const getProviderData = (common: Common, provider: ProviderType, collection: CollectionType): ProviderGamesModel => {
    const jwt = common.session.currentJwt;
    const gatewayUrl = common.appEnvParams.gatewayUrl;

    const resource = createProviderGamesResource(async () => {
        const response = await apiGetGamesRequest(
            gatewayUrl,
            {
                requestBody: {
                    providerSlug: provider,
                    collectionSlug: collection,
                    // TODO: add in phase 2 infinite scroll
                    pageSize: 9999,
                },
            },
            {
                authorization: `Bearer ${jwt}`,
            }
        );

        if (response.status === 200) {
            return convertNewGameModelToLegacy(response.body);
        }
        return [];
    });

    return new ProviderGamesModel(resource, provider);
};

export class ProviderStore {
    private providerData: AutoMap<CollectionType, AutoMap<ProviderType, ProviderGamesModel>>;

    public constructor(private common: Common) {
        const configState = ConfigState.get(this.common);
        const { casinoStoreEnabled } = configState.config;
        this.providerData = new AutoMap(
            (collection: CollectionType) =>
                new AutoMap((provider: ProviderType) => {
                    return casinoStoreEnabled
                        ? getProviderData(common, provider, collection)
                        : getProviderDataLegacy(this.common.trpcClient, provider, configState);
                })
        );
    }

    public getProviderData(provider: ProviderType, collection: CollectionType): ProviderGamesModel {
        return this.providerData.get(collection).get(provider);
    }
}
